import type { CrosswordWord, GameMode, SubmittedAnswer } from '../types';
import { wordNumber } from '../word-label';

interface CluePanelProps {
  words: CrosswordWord[];
  mode: GameMode;
  selectedWordId: string | null;
  submittedAnswers: Record<string, SubmittedAnswer>;
  onSelectWord: (word: CrosswordWord) => void;
}

// Mirrors the server's base score (DESIGN §4.2); the live time bonus can add
// up to the same amount again.
function basePoints(word: CrosswordWord): number {
  return 10 + word.difficulty * 5;
}

// Letters already on the board from accepted crossing words shrink a word's
// prize, mirroring the server's discount.
function currentValue(
  word: CrosswordWord,
  words: CrosswordWord[],
  submittedAnswers: Record<string, SubmittedAnswer>
): number {
  const filled = new Set<string>();
  for (const other of words) {
    if (other.id === word.id || !submittedAnswers[other.id]?.is_correct) continue;
    for (const [row, col] of other.word_positions) filled.add(`${row}:${col}`);
  }
  const revealed = word.word_positions.filter(([row, col]) => filled.has(`${row}:${col}`)).length;
  if (revealed === 0) return basePoints(word);
  return Math.max(1, Math.round((basePoints(word) * (word.length - revealed)) / word.length));
}

function ClueList({
  title,
  words,
  allWords,
  mode,
  selectedWordId,
  submittedAnswers,
  onSelectWord,
}: CluePanelProps & { title: string; allWords: CrosswordWord[] }) {
  return (
    <div className="clue-list">
      <h3>{title}</h3>
      {words.map((word) => {
        const submission = submittedAnswers[word.id];
        const done = submission?.is_correct;
        return (
          <button
            className={`clue ${selectedWordId === word.id ? 'active' : ''} ${done ? 'done' : ''}`}
            key={word.id}
            onClick={() => onSelectWord(word)}
            type="button"
          >
            <span className="clue-number">{wordNumber(word)}</span>
            <span className="clue-text">{word.clue}</span>
            {mode === 'competitive' && (
              <span className="clue-points">
                {done ? `+${submission.points}` : `${currentValue(word, allWords, submittedAnswers)} pts`}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

export function CluePanel(props: CluePanelProps) {
  const across = props.words.filter((word) => word.direction === 'across');
  const down = props.words.filter((word) => word.direction === 'down');

  return (
    <section className="clue-panel" aria-label="Clues">
      <div className="clue-columns">
        <ClueList {...props} allWords={props.words} title="Across" words={across} />
        <ClueList {...props} allWords={props.words} title="Down" words={down} />
      </div>
    </section>
  );
}
